import React, { useState } from "react";
import { Dialog } from "@/components/main/utils/Dialog";
import { Button } from "@/components/common/Button";
import { Stack } from "@/components/common/Stack";
import { FormField } from "@/components/common/FormField";
import { TextInput } from "@/components/common/TextInput";
import { Dataset, createDataset } from "@/lib/api/datasets";
import { showNotificationGlobal } from "@/stores/useNotificationStore";

const MAX_NAME = 200;

interface CreateDatasetDialogProps {
  open: boolean;
  onClose: () => void;
  /// Called with the created dataset so the page can prepend it to the list.
  onCreated: (dataset: Dataset) => void;
  /// Create a role-owned catalog dataset instead of a personal one. Server-gated
  /// on `can_manage_public_catalog`.
  catalog?: boolean;
}

/// Create an empty dataset (name + optional description). Membership is added
/// later — a fresh dataset is an empty shell owned by the caller.
export const CreateDatasetDialog: React.FC<CreateDatasetDialogProps> = ({
  open,
  onClose,
  onCreated,
  catalog = false,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && trimmed.length <= MAX_NAME && !saving;

  const reset = (): void => {
    setName("");
    setDescription("");
    setSaving(false);
  };

  const close = (): void => {
    if (saving) return;
    reset();
    onClose();
  };

  const submit = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    try {
      const dataset = await createDataset(trimmed, description, catalog);
      showNotificationGlobal(`Created "${dataset.name}"`, "success");
      onCreated(dataset);
      reset();
      onClose();
    } catch {
      showNotificationGlobal("Failed to create dataset", "error");
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      title={catalog ? "New catalog dataset" : "New dataset"}
      onClose={close}
      actions={
        // Dismiss is the header's Close (X) / Esc — no redundant Cancel button.
        <Button
          variant="primary"
          onClick={() => void submit()}
          disabled={!canSave}
          loading={saving}
        >
          Create
        </Button>
      }
    >
      <Stack gap="md">
        <p className="text-small text-fg-muted">
          {catalog
            ? "A catalog dataset is owned by the role, not by you — every catalog manager co-manages it. It stays private until it's released, and releasing it publishes it."
            : "A dataset is a shareable set of tables. Create it now, then add tables you can access. Sharing grants the list, never the tables."}
        </p>
        <FormField label="Name" htmlFor="dataset-name" required>
          <TextInput
            name="dataset-name"
            value={name}
            onChange={setName}
            placeholder="e.g. National Statistics"
          />
        </FormField>
        <FormField label="Description" htmlFor="dataset-description">
          <TextInput
            name="dataset-description"
            value={description}
            onChange={setDescription}
            placeholder="Optional"
            multiline
            minRows={2}
          />
        </FormField>
      </Stack>
    </Dialog>
  );
};
